import React from 'react';
import { connect } from 'react-redux';
import ReactComponentWrapper from '../ReactComponentWrapper';
import { setBadgesPageView, requestBadgeAttributes, updateBadgeLevelUpViewed } from '../actions/badgesActions';
import { dismissNotification } from 'actions/notificationActions';
import { showModal } from '../actions';
import BadgeDetails from '../components/badges/BadgeDetails';
import { getBadges } from '../reducers/badge/badges';
import {
    getNotificationDismissSuccessWithId,
    getNotificationDismissFailWithId
} from 'reducers/notification/notification';
import { VIEW } from '../Badges/constants/badgesPageConstants';
import { localizeBadgeTitleReact } from '../utils/BadgeUtils';
import NavigationUtil from 'utils/NavigationUtil';
import DateParser from 'utils/date/DateParser';
import DateFormatter from 'utils/date/DateFormatter';
import Translate from 'components/Translate';
import i18next from 'i18next';
import { withTranslation } from 'react-i18next';
import styles from './BadgeNotification.module.css';

const mapStateToProps = (state, props) => ({
    badgesAttributes: getBadges(state).attributes,
    notificationDismissSuccess: getNotificationDismissSuccessWithId(state, props.badgeNotification.notificationId),
    notificationDismissFail: getNotificationDismissFailWithId(state, props.badgeNotification.notificationId)
});

const mapDispatchToProps = {
    showModal,
    setBadgesPageView,
    requestBadgeAttributes,
    dismissNotification
};

export const BADGE_NOTIFICATION_TYPES = {
    BADGE_START_SINGLE: 'BADGE_START_SINGLE',
    BADGE_START: 'BADGE_START',
    BADGE_START_TODAY_ONLY_SINGLE: 'BADGE_START_TODAY_ONLY_SINGLE',
    BADGE_START_TODAY_ONLY: 'BADGE_START_TODAY_ONLY',
    BADGE_EXPIRE_SINGLE: 'BADGE_EXPIRE_SINGLE',
    BADGE_EXPIRE: 'BADGE_EXPIRE',
    BADGE_EARNED_SINGLE: 'BADGE_EARNED_SINGLE',
    BADGE_EARNED: 'BADGE_EARNED',
    BADGE_LEVEL_UP: 'BADGE_LEVEL_UP',
    PARTNER_REWARD_EARNED: 'PARTNER_REWARD_EARNED'
};

class BadgeNotification extends React.Component {
    constructor(props) {
        super(props);
        this.dismissRef = React.createRef();
        this.undoRef = React.createRef();
        this.state = {
            showUndo: false
        };
    }

    componentDidMount() {
        const { badgesAttributes, displayName, requestBadgeAttributes } = this.props;

        //the react events are added to the document and inside the TopHeaderBarView (resource-js), handleDropdownContentClick method we have an e.stopPropagation()
        //that's why the events from this component are not triggered and we need to manually add the events (like the below click)
        this.badgeNotification.addEventListener('click', this.onBadgeNotificationClick.bind(this));
        if (this.dismissRef.current) this.dismissRef.current.addEventListener('click', this.onDismissClick.bind(this));
        if (this.undoRef.current) this.undoRef.current.addEventListener('click', this.onUndoClick.bind(this));

        if (!badgesAttributes.data) {
            requestBadgeAttributes(displayName);
        }
    }

    componentDidUpdate(prevProps) {
        const { notificationDismissSuccess, notificationDismissFail, onError, onResetError } = this.props;

        if (prevProps.notificationDismissSuccess !== notificationDismissSuccess && notificationDismissSuccess) {
            onResetError();
        }

        if (prevProps.notificationDismissFail !== notificationDismissFail && notificationDismissFail) {
            onError();
            clearTimeout(this.dismissTimer);
            this.dismissTimer = null;
            this.setState({ showUndo: false });
        }
    }

    onDismissClick(e) {
        const {
            badgeNotification,
            onSlideUp,
            onNotificationDismiss,
            dismissNotification,
            addToList,
            removeFromList
        } = this.props;
        const DISMISS_TIMEOUT = 5000;
        e.preventDefault();
        this.setState({ showUndo: true });
        dismissNotification({ notificationId: badgeNotification.notificationId, dismiss: true });
        this.dismissTimer = setTimeout(() => {
            onSlideUp();
            onNotificationDismiss(badgeNotification.notificationId);
            removeFromList(badgeNotification.notificationId);
        }, DISMISS_TIMEOUT);
        addToList(badgeNotification.notificationId, () => this.clearTimerAndExecute());
    }

    onUndoClick(e) {
        const { badgeNotification, dismissNotification, removeFromList } = this.props;
        e.preventDefault();
        this.setState({ showUndo: false });
        clearTimeout(this.dismissTimer);
        this.dismissTimer = null;
        dismissNotification({ notificationId: badgeNotification.notificationId, dismiss: false });
        removeFromList(badgeNotification.notificationId);
    }

    clearTimerAndExecute() {
        const { onSlideUp, onNotificationDismiss, badgeNotification } = this.props;
        if (this.dismissTimer) {
            clearTimeout(this.dismissTimer);
            this.dismissTimer = null;
            onSlideUp();
            onNotificationDismiss(badgeNotification.notificationId);
        }
    }

    render() {
        const { badgesAttributes } = this.props;
        this.badgeNotificationData = this.getBadgeNotificationData();

        if (!badgesAttributes) {
            return null;
        }

        return (
            <ReactComponentWrapper {...this.props}>
                <a href="" ref={(elem) => (this.badgeNotification = elem)} className="notification-body">
                    {this.badgeNotificationData.message}
                    <div className="date-stamp">{this.formatMessageDate()}</div>
                </a>
                <button ref={this.dismissRef} className="icon-dismiss" />
                <div className={`undo-section ${this.state.showUndo ? styles.fadeIn : styles.fadeOut}`}>
                    <div>
                        <Translate ns="notification" content="notification_removed" />
                    </div>
                    <button ref={this.undoRef} className="undoButton">
                        <Translate ns="notification" content="weight.scale.decline.undo.button" />
                    </button>
                </div>
            </ReactComponentWrapper>
        );
    }

    onBadgeNotificationClick(e) {
        e.preventDefault();

        // this is in order to close the notifications menu when a badge notification is clicked and the user is on the badges page
        document.body.click();

        const { displayName, badgeNotification, badgesAttributes, setBadgesPageView, showModal } = this.props;

        switch (this.badgeNotificationData.view) {
            case 'availableBadges':
                NavigationUtil.navigate('/badges', e);
                setBadgesPageView(VIEW.AVAILABLE, displayName);
                break;
            case 'earnedBadges':
                if (badgeNotification.type === BADGE_NOTIFICATION_TYPES.BADGE_LEVEL_UP) {
                    updateBadgeLevelUpViewed();
                }
                NavigationUtil.navigate('/badges', e);
                setBadgesPageView(VIEW.EARNED, displayName);
                break;
            case 'badgeDetail':
                let badge = {};
                badge.badgeId = badgeNotification.relativeNameValue.badge_id;
                let view = <BadgeDetails {...this.props} badge={badge} badgesAttributes={badgesAttributes.data} />;

                showModal({
                    view,
                    title: 'badges:badge_detail'
                });

                break;
            default:
                break;
        }
    }

    getBadgeNotificationData() {
        const { badgeNotification } = this.props;

        // The view can also be 'badgeDetail' which opens the badge detail modal on whichever page the user is.
        // usage: return {message: <Translate ns='notification' content='badge_start_single' />, view: 'badgeDetail'};
        switch (badgeNotification.type) {
            case BADGE_NOTIFICATION_TYPES.BADGE_START_SINGLE:
                return {
                    message: <Translate ns="notification" content="badge_start_single" />,
                    view: 'availableBadges'
                };
            case BADGE_NOTIFICATION_TYPES.BADGE_START:
                return { message: <Translate ns="notification" content="badge_start" />, view: 'availableBadges' };
            case BADGE_NOTIFICATION_TYPES.BADGE_START_TODAY_ONLY_SINGLE:
                return {
                    message: <Translate ns="notification" content="badge_start_today_only_single" />,
                    view: 'availableBadges'
                };
            case BADGE_NOTIFICATION_TYPES.BADGE_START_TODAY_ONLY:
                return {
                    message: <Translate ns="notification" content="badge_start_today_only" />,
                    view: 'availableBadges'
                };
            case BADGE_NOTIFICATION_TYPES.BADGE_EXPIRE_SINGLE:
                return {
                    message: (
                        <Translate
                            ns="notification"
                            content="badge_expire_single"
                            params={{
                                0: localizeBadgeTitleReact(badgeNotification.relativeNameValue.badge_key, i18next)
                            }}
                        />
                    ),
                    view: 'availableBadges'
                };
            case BADGE_NOTIFICATION_TYPES.BADGE_EXPIRE:
                return { message: <Translate ns="notification" content="badge_expire" />, view: 'availableBadges' };
            case BADGE_NOTIFICATION_TYPES.BADGE_EARNED_SINGLE:
                return {
                    message: (
                        <Translate
                            ns="notification"
                            content="badge_earned_single"
                            params={{
                                0: localizeBadgeTitleReact(badgeNotification.relativeNameValue.badge_key, i18next)
                            }}
                        />
                    ),
                    view: 'earnedBadges'
                };
            case BADGE_NOTIFICATION_TYPES.BADGE_EARNED:
                return {
                    message: (
                        <Translate
                            ns="notification"
                            content="badge_earned"
                            params={{ 0: badgeNotification.relativeNameValue.count }}
                        />
                    ),
                    view: 'earnedBadges'
                };
            case BADGE_NOTIFICATION_TYPES.BADGE_LEVEL_UP:
                return {
                    message: (
                        <Translate
                            ns="notification"
                            content="badge_level_up"
                            params={{ 0: badgeNotification.relativeNameValue.level }}
                        />
                    ),
                    view: 'earnedBadges'
                };
            case BADGE_NOTIFICATION_TYPES.PARTNER_REWARD_EARNED:
                return {
                    message: (
                        <Translate
                            ns="challengewidget"
                            content="partner_challenge_badge_complete_success"
                            params={{
                                0: localizeBadgeTitleReact(badgeNotification.relativeNameValue.badge_key, i18next)
                            }}
                        />
                    ),
                    view: 'earnedBadges'
                };
            default:
                break;
        }
    }

    formatMessageDate() {
        const { badgeNotification } = this.props;
        let localDate = DateParser.parseISOUTC(badgeNotification.dateTimeString);

        return DateFormatter.formatTimeAgo(localDate);
    }

    componentWillUnmount() {
        this.badgeNotification.removeEventListener('click', this.onBadgeNotificationClick);
        if (this.dismissRef.current) this.dismissRef.current.removeEventListener('click', this.onDismissClick);
        if (this.undoRef.current) this.undoRef.current.removeEventListener('click', this.onUndoClick);
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation('badges')(BadgeNotification));
